import type { ApplicationStage, PipelineBoardResponse, PipelineListResponse, PipelineRow, TagOption } from '@nest-recruit/shared';

// What an action is expected to do to the rows on screen, drawn before the server
// answers (CLAUDE.md §5a rule 1). Pure, so the arithmetic a board's counts depend on
// is tested without a browser.

export interface RowPatch {
  readonly stage?: ApplicationStage;
  readonly addTags?: readonly TagOption[];
  readonly recruiter?: { readonly id: string; readonly name: string };
  readonly isPooled?: boolean;
}

export type PipelineData = PipelineListResponse | PipelineBoardResponse;

export function patched(data: PipelineData, ids: ReadonlySet<string>, patch: RowPatch): PipelineData {
  const apply = (row: PipelineRow): PipelineRow => (ids.has(row.id) ? patchRow(row, patch) : row);
  if ('rows' in data) return { ...data, rows: data.rows.map(apply) };
  const moved = patch.stage ? data.cards.filter((card) => ids.has(card.id) && card.stage !== patch.stage) : [];
  // The column counts follow the move too, so a card never sits under a count that says it is not there.
  const columns = data.columns.map((column) => {
    const leaving = moved.filter((card) => card.stage === column.stage).length;
    const arriving = patch.stage === column.stage ? moved.length : 0;
    return leaving === 0 && arriving === 0 ? column : { ...column, count: Math.max(0, column.count - leaving + arriving) };
  });
  return {
    ...data,
    columns,
    cards: data.cards.map(apply),
    rejectedCount: data.rejectedCount + (patch.stage === 'rejected' ? moved.length : 0),
  };
}

function patchRow(row: PipelineRow, patch: RowPatch): PipelineRow {
  return {
    ...row,
    ...(patch.stage ? { stage: patch.stage } : {}),
    ...(patch.recruiter ? { recruiterUserId: patch.recruiter.id, recruiterName: patch.recruiter.name } : {}),
    ...(patch.addTags ? { tags: [...row.tags, ...patch.addTags.filter((tag) => !row.tags.some((existing) => existing.id === tag.id))] } : {}),
    ...(patch.isPooled ? { flags: { ...row.flags, isPooled: true } } : {}),
  };
}
